import { useState } from "react";
import { BarChart3 } from "lucide-react";
import { Card } from "./Card";

/* Donut chart for distribution metrics. Compact enough to fit in a
   4-column dashboard row while still reading clearly.
   - Ring chart with hover-grow on segments
   - Centered total + label in the donut hole; swaps to hovered segment
     count + percent on hover
   - Side legend with color dot, label, count, and percent for each segment
   - Hovering a segment OR a legend row dims the others
   - Graceful empty state

   `data` shape: [{ label, value, cls }] — same as MiniBarChart so it's
   a drop-in. `cls` is a Tailwind text-color class; we wrap the segment
   in a <g> with that class and use currentColor for fill. */
/* Header palettes. Literal class strings only — see the JIT note in Card.jsx. */
// Header sits one shade above the card body so the strip still reads as a
// header once the body itself is tinted.
const HEADERS = {
  default: "bg-slate-100 border-slate-200",
  info:    "bg-blue-100 border-blue-200",
  violet:  "bg-violet-100 border-violet-200",
  teal:    "bg-teal-100 border-teal-200",
  amber:   "bg-amber-100 border-amber-200",
};
const CARD_TONES = {
  default: "default", info: "info", violet: "violet", teal: "teal", amber: "warning",
};
const HEADER_CHIPS = {
  default: "bg-slate-400",
  info:    "bg-blue-500",
  violet:  "bg-violet-500",
  teal:    "bg-teal-500",
  amber:   "bg-amber-500",
};

export function DonutChart({ data, title, accent = "default" }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const total = data.reduce((a, d) => a + d.value, 0);

  const size = 96;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 44;
  const innerR = 30;

  let cursor = -Math.PI / 2;
  const segments = data.map((d, i) => {
    const portion = total > 0 ? d.value / total : 0;
    const a0 = cursor;
    const a1 = cursor + portion * Math.PI * 2;
    cursor = a1;
    return { ...d, i, portion, a0, a1, pct: total > 0 ? Math.round(portion * 100) : 0 };
  });

  const arcPath = (a0, a1) => {
    const isFullCircle = Math.abs(a1 - a0) >= Math.PI * 2 - 1e-6;
    if (isFullCircle) {
      return [
        `M ${cx + outerR} ${cy}`,
        `A ${outerR} ${outerR} 0 1 1 ${cx - outerR} ${cy}`,
        `A ${outerR} ${outerR} 0 1 1 ${cx + outerR} ${cy}`,
        `L ${cx + innerR} ${cy}`,
        `A ${innerR} ${innerR} 0 1 0 ${cx - innerR} ${cy}`,
        `A ${innerR} ${innerR} 0 1 0 ${cx + innerR} ${cy}`,
        `Z`,
      ].join(" ");
    }
    const x0o = cx + outerR * Math.cos(a0);
    const y0o = cy + outerR * Math.sin(a0);
    const x1o = cx + outerR * Math.cos(a1);
    const y1o = cy + outerR * Math.sin(a1);
    const x0i = cx + innerR * Math.cos(a1);
    const y0i = cy + innerR * Math.sin(a1);
    const x1i = cx + innerR * Math.cos(a0);
    const y1i = cy + innerR * Math.sin(a0);
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    return [
      `M ${x0o} ${y0o}`,
      `A ${outerR} ${outerR} 0 ${large} 1 ${x1o} ${y1o}`,
      `L ${x0i} ${y0i}`,
      `A ${innerR} ${innerR} 0 ${large} 0 ${x1i} ${y1i}`,
      `Z`,
    ].join(" ");
  };

  return (
    <Card tone={CARD_TONES[accent] || "default"} className="overflow-hidden h-full flex flex-col">
      {/* Header strip carries the panel's identity colour; the body stays
          white so the donut and legend keep full contrast. Tinting behind
          a chart fights the chart. */}
      <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${HEADERS[accent] || HEADERS.default}`}>
        <span className={`w-6 h-6 rounded-md grid place-items-center shrink-0 ${HEADER_CHIPS[accent] || HEADER_CHIPS.default}`}>
          <BarChart3 className="w-3.5 h-3.5 text-white" />
        </span>
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-900 leading-tight">{title}</h4>
          {total > 0 && (
            <p className="text-[10px] text-slate-500">{segments.length} categor{segments.length === 1 ? "y" : "ies"}</p>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 min-h-0 flex flex-col">

      {total === 0 || data.length === 0 ? (
        <div className="py-8 text-center">
          <BarChart3 className="w-7 h-7 text-slate-300 mx-auto mb-1.5" />
          <div className="text-xs text-slate-400">No data to display</div>
        </div>
      ) : (
        <div className="flex items-start gap-3 flex-1 min-h-0">
          <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg
              width={size}
              height={size}
              viewBox={`0 0 ${size} ${size}`}
              onMouseLeave={() => setHoverIdx(null)}
            >
              {segments.map((s) => {
                const isHover = hoverIdx === s.i;
                const isDim = hoverIdx != null && !isHover;
                return (
                  <g
                    key={s.label}
                    className={s.cls || "text-blue-600"}
                    style={{
                      opacity: isDim ? 0.3 : 1,
                      transition: "opacity 150ms ease, transform 150ms ease",
                      transformOrigin: `${cx}px ${cy}px`,
                      transform: isHover ? "scale(1.05)" : "scale(1)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={() => setHoverIdx(s.i)}
                  >
                    <path d={arcPath(s.a0, s.a1)} fill="currentColor" />
                  </g>
                );
              })}
            </svg>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                {hoverIdx != null ? (
                  <>
                    <div className="text-base font-bold text-slate-900 tabular-nums leading-none">{segments[hoverIdx].value}</div>
                    <div className="text-[9px] text-slate-500 mt-0.5 tabular-nums">{segments[hoverIdx].pct}%</div>
                  </>
                ) : (
                  <>
                    <div className="text-base font-bold text-slate-900 tabular-nums leading-none">{total}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wide mt-0.5">Total</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Height comes from the card, not a magic number: the legend
              fills whatever the tallest panel in the row leaves it, so a
              10-category status breakdown uses the space instead of
              scrolling inside a half-empty card. */}
          <div className="flex-1 min-w-0 space-y-1 overflow-y-auto -mr-1 pr-1 h-full">
            {segments.map((s) => {
              const isHover = hoverIdx === s.i;
              const isDim = hoverIdx != null && !isHover;
              return (
                <div
                  key={s.label}
                  className={`flex items-center gap-1.5 py-0.5 px-1 -mx-1 rounded transition-opacity cursor-pointer ${isHover ? "bg-slate-50" : ""}`}
                  style={{ opacity: isDim ? 0.5 : 1 }}
                  onMouseEnter={() => setHoverIdx(s.i)}
                  onMouseLeave={() => setHoverIdx(null)}
                >
                  <span className={`w-2 h-2 rounded-sm shrink-0 ${s.cls ? s.cls.replace("text-", "bg-") : "bg-blue-600"}`} />
                  <span className="text-[11px] text-slate-700 truncate flex-1" title={s.label}>{s.label}</span>
                  <span className="text-[11px] text-slate-900 font-semibold tabular-nums">{s.value}</span>
                  <span className="text-[10px] text-slate-400 tabular-nums w-7 text-right">{s.pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </Card>
  );
}
